// Content-blind typing provenance for the online survey — the browser twin of
// jolive_runtime/smart_question/provenance.py. Records HOW an answer is typed
// (keystroke timing, paste sizes, focus changes), NEVER the text. summarize()
// returns the same shape as the Python recorder so the sq_provenance column is
// identical whether a participant ran online or in-lab.
//
// Only used when the participant was shown the biometrics notice (provenance on);
// see PROVENANCE_NOTICE_FLAG below.

// Set by BiometricsNoticeRenderer when it mounts (which only happens when the
// researcher enabled provenance). SurveyForm reads it to decide whether to
// capture + send provenance at all.
export const PROVENANCE_NOTICE_FLAG = "jol_provenance_notice";

export function provenanceNoticeShown(): boolean {
  try {
    return sessionStorage.getItem(PROVENANCE_NOTICE_FLAG) === "1";
  } catch {
    return false;
  }
}

function round(n: number, d: number): number {
  const f = 10 ** d;
  return Math.round(n * f) / f;
}

// Monotonic seconds (performance.now avoids wall-clock / DST issues).
function nowSec(): number {
  return performance.now() / 1000;
}

export class ProvenanceRecorder {
  private startedAt: number | null = null;
  private keyTimes: number[] = [];
  private pasteEvents: { chars: number; at_offset_s: number }[] = [];
  private focusLost = 0;
  private focusRegained = 0;
  private seenFocus = false;

  private start(t: number): void {
    if (this.startedAt === null) this.startedAt = t;
  }

  private offset(t: number): number {
    return this.startedAt === null ? 0 : round(t - this.startedAt, 3);
  }

  recordKey(): void {
    const t = nowSec();
    this.start(t);
    this.keyTimes.push(t);
  }

  recordPaste(charCount: number): void {
    const t = nowSec();
    this.start(t);
    this.pasteEvents.push({
      chars: Math.max(0, Math.trunc(charCount)),
      at_offset_s: this.offset(t),
    });
  }

  // First focus is the initial entry (not a "regain"); later ones count.
  recordFocus(): void {
    if (this.seenFocus) this.focusRegained += 1;
    this.seenFocus = true;
  }

  recordBlur(): void {
    this.focusLost += 1;
  }

  summarize(finalText: string): Record<string, unknown> {
    const length = (finalText || "").length;
    const total = this.offset(nowSec());

    const intervals: number[] = [];
    for (let i = 1; i < this.keyTimes.length; i++) {
      intervals.push(round(this.keyTimes[i] - this.keyTimes[i - 1], 3));
    }
    const mean =
      intervals.length > 0
        ? round(intervals.reduce((a, b) => a + b, 0) / intervals.length, 3)
        : null;
    let burstiness: number | null = null;
    if (intervals.length >= 2 && mean) {
      const varr =
        intervals.reduce((a, x) => a + (x - mean) ** 2, 0) / intervals.length;
      burstiness = round(Math.sqrt(varr) / mean, 3);
    }

    const pastedChars = this.pasteEvents.reduce((a, p) => a + p.chars, 0);
    const charsPerSecond = total && total > 0 ? round(length / total, 2) : null;

    return {
      total_time_s: total,
      final_length: length,
      keystrokes: this.keyTimes.length,
      mean_inter_key_interval_s: mean,
      burstiness,
      paste_events: this.pasteEvents,
      pasted_chars: pastedChars,
      typed_vs_pasted:
        pastedChars > Math.max(1, length) * 0.5 ? "mostly pasted" : "mostly typed",
      focus_lost: this.focusLost,
      focus_regained: this.focusRegained,
      chars_per_second: charsPerSecond,
    };
  }
}
